<template>
  <div class="step-tab">
    <div class="cp-card">
      <!-- Box visibility for this step (reflected in the preview below). -->
      <BoxVisibilityToggle
          :boxVisibility="profile.box_visibility"
          boxKey="custom_terms"
          :label="L.review_box_custom_terms || 'Terms & data protection'"
          :hint="L.box_toggle_custom_terms_hint || 'Custom AGB / privacy link list + consent checkbox.'" />
      <BoxVisibilityToggle
          :boxVisibility="profile.box_visibility"
          boxKey="passolution"
          :label="L.review_box_passolution || 'Visa / entry requirements'"
          :hint="L.box_toggle_passolution_hint || 'Visa / entry / vaccination notice with acceptance.'" />

      <!-- Headings & labels -->
      <CollapsibleSection :title="L.checkout_texts_labels || 'Labels & headings'">
        <div class="cp-grid">
          <CustomInput :label="L.checkout_text_agb_heading || 'Terms section heading'" v-model="text.checkout_agb_heading" />
          <CustomInput :label="L.checkout_text_name_heading || 'Name disclaimer heading'" v-model="text.checkout_name_heading" />
          <CustomInput :label="L.checkout_text_name_confirm || 'Name confirmation text'" v-model="text.checkout_name_confirm" />
        </div>
      </CollapsibleSection>

      <!-- Legal links -->
      <CollapsibleSection :title="L.checkout_texts_links || 'Legal links'" :defaultOpen="false">
        <div class="cp-grid">
          <CustomInput :label="L.checkout_text_agb_link || 'AGB link label'" v-model="text.checkout_agb_link" />
          <CustomInput :label="L.checkout_text_agb_url || 'AGB link URL'" v-model="text.agbs" />
          <CustomInput :label="L.checkout_text_datenschutz_link || 'Privacy link label'" v-model="text.checkout_datenschutz_link" />
          <CustomInput :label="L.checkout_text_datenschutz_url || 'Privacy link URL'" v-model="text.datenschutz" />
          <CustomInput :label="L.checkout_text_formblatt_link || 'Formblatt link label'" v-model="text.checkout_formblatt_link" />
          <CustomInput :label="L.checkout_text_formblatt_pdf || 'Formblatt link URL'" v-model="text.formblatt_pdf" />
          <CustomInput :label="L.checkout_text_insolvenz_link || 'Insolvency link label'" v-model="text.checkout_insolvenz_link" />
          <CustomInput :label="L.checkout_text_insolvenz_pdf || 'Insolvency link URL'" v-model="text.insolvenz_pdf" />
        </div>
      </CollapsibleSection>

      <!-- Confirmation / long-form texts -->
      <CollapsibleSection :title="L.checkout_texts_longform || 'Confirmation texts'" :defaultOpen="false">
        <div class="cp-richfield" v-for="rt in richFields" :key="rt.key">
          <label class="cp-label">{{ L[rt.langKey] || rt.fallback }}</label>
          <div class="xi-editor">
            <TextEditorBox class="branding-tabs" :options="options" :editor="editors[rt.key]" :image="true" />
            <editor-content class="xi-editor-content" :editor="editors[rt.key]" />
          </div>
        </div>
      </CollapsibleSection>
    </div>

    <PreviewFrame :label="L.preview || 'Preview'">
      <TermsPreview :texts="text" :rich="richHtml" :lang="lang" :brandColor="brandColor" :boxVisibility="profile.box_visibility" />
    </PreviewFrame>
  </div>
</template>

<script>
import { EditorContent } from '@tiptap/vue-2'
import TextEditorBox from '@/components/basics/TextEditorBox.vue'
import CollapsibleSection from '../../parts/CollapsibleSection.vue'
import CustomInput from '../../parts/CustomInput.vue'
import PreviewFrame from './previews/PreviewFrame.vue'
import TermsPreview from './previews/TermsPreview.vue'
import BoxVisibilityToggle from './BoxVisibilityToggle.vue'

/**
 * Terms step tab: the legal/consent texts the profile drives on the checkout's
 * Boomerang custom-terms layout — AGB heading, legal link labels + URLs, name
 * disclaimer, and the rich confirmation texts (insurance / AGB consent / name).
 * Live TermsPreview below. Parent owns `text` (texts[lang]) and the editors map.
 */
export default {
  name: 'StepTabTerms',
  components: { EditorContent, TextEditorBox, CollapsibleSection, CustomInput, PreviewFrame, TermsPreview, BoxVisibilityToggle },
  props: {
    options: { type: Object, required: true },
    profile: { type: Object, required: true },       // holds box_visibility (mutated in place)
    text: { type: Object, required: true },
    editors: { type: Object, default: () => ({}) }, // { fieldKey: tiptap editor }
    richFields: { type: Array, default: () => [] },  // [{ key, langKey, fallback }] subset for this step
    richHtml: { type: Object, default: () => ({}) },
    lang: { type: String, default: 'de' },
    brandColor: { type: String, default: '#1a73e8' }
  },
  computed: {
    L() { return this.options.Lang || {}; }
  }
};
</script>

<style scoped>
.step-tab { animation: cp-fade .2s ease; }
@keyframes cp-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }

.cp-card { background: #fff; border-radius: 1rem; box-shadow: 0 2px 12px rgba(0,0,0,0.06); padding: 1.5rem 1.75rem; }
.cp-label { display: block; font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 0.25rem; }
.cp-input { width: 100%; border: 1px solid #dcdcdc; border-radius: 8px; padding: 0.55rem 0.75rem; font-size: 0.9rem; background: #fafafa; box-sizing: border-box; }
.cp-input:focus-within, .cp-input:focus { outline: none; border-color: #999; background: #fff; }
.cp-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem 1.5rem; }
@media (max-width: 800px) { .cp-grid { grid-template-columns: 1fr; } }
.cp-field { display: flex; flex-direction: column; min-width: 0; }
.cp-richfield { margin-bottom: 1.25rem; }
</style>
<!-- Shared rich-editor box look — matches Edit Branding ▸ Additional Information. -->
<style lang="scss">
@use "../../../assets/style/General/richEditorBox.scss";
</style>
